/**
 * bQuest Recommended Resources curated section (Feature 083)
 * Self-contained: fetches the global curated list and the care seeker's saved
 * resources (React Query dedupes both), then renders below the saved resources
 * on the My Team → Resources tab. Hidden when empty.
 */

import { Link } from 'react-router-dom';
import { BookOpen, Bookmark } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { CuratedSection } from './CuratedSection';
import {
  useCuratedTeam,
  useAllFavorites,
  useAddFavoriteResource,
  useRemoveFavoriteResource,
} from '../hooks/useFavorites';

const SUBTITLE =
  'Guides and collateral from our preferred partners, hand-picked to help you plan ahead.';

export function CuratedResourcesSection() {
  const { data: curated, isLoading, isError } = useCuratedTeam();
  const { data: favorites } = useAllFavorites();
  const addFavorite = useAddFavoriteResource();
  const removeFavorite = useRemoveFavoriteResource();

  if (isLoading) {
    return (
      <div className="mt-8 space-y-3" data-testid="curated-resources-skeleton">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="mt-8 p-6 text-center">
        <p className="text-red-600">We couldn&apos;t load bQuest Recommended Resources right now.</p>
      </Card>
    );
  }

  const resources = curated?.recommendedResources ?? [];
  if (resources.length === 0) {
    return null;
  }

  const savedIds = new Set((favorites?.resources ?? []).map((f) => f.resource.id));

  const toggleSave = (resourceId: string, isSaved: boolean) => {
    if (isSaved) {
      removeFavorite.mutate(resourceId);
    } else {
      addFavorite.mutate({ resourceId });
    }
  };

  return (
    <CuratedSection title="bQuest Recommended Resources" subtitle={SUBTITLE}>
      <div className="space-y-4">
        {resources.map((resource) => {
          const isSaved = savedIds.has(resource.id);
          return (
            <Card key={resource.id} className="p-6 bg-white">
              <div className="flex items-start justify-between">
                <Link
                  to={`/resource-hub-detail/${encodeURIComponent(resource.seoUrl)}`}
                  className="flex items-start gap-4 flex-1 group"
                >
                  <div className="w-12 h-12 rounded bg-[#026857]/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-6 h-6 text-[#026857]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg text-gray-900 mb-1 group-hover:underline">
                      {resource.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-semibold uppercase tracking-wide text-[#026857]">
                        {resource.type.name}
                      </span>
                      {resource.partnerName && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span>{resource.partnerName}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={isSaved ? 'Remove from saved' : 'Save resource'}
                  onClick={() => toggleSave(resource.id, isSaved)}
                >
                  <Bookmark
                    aria-hidden="true"
                    className={
                      isSaved ? 'w-4 h-4 fill-[#026857] text-[#026857]' : 'w-4 h-4 text-gray-400'
                    }
                  />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </CuratedSection>
  );
}
